import { useState } from "react";
import { Button } from "@/core/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/core/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/core/components/ui/tooltip";
import type { PayrollEditorQueries } from "./query";
import { proposalError } from "./state";
import type { Workspace } from "./types";

/**
 * The owner putting their own proposal live.
 *
 * It used to say saved changes took effect "only after Volca review and
 * publication", and no review existed — no queue, no notification, nothing
 * watching for a saved proposal. The owner can now finish what they started.
 *
 * One condition, and only where it means something: you have to have seen what
 * the change does to pay. A proposal that moved the wording cannot move a
 * number, so there is nothing to look at and nothing to ask for.
 */
export const PublishProposal = ({
  workspace,
  publish,
  comparisonReady,
  comparisonPossible,
  busy,
}: {
  workspace: Workspace;
  publish: PayrollEditorQueries["publish"];
  /** A comparison has run on this exact proposal. */
  comparisonReady: boolean;
  /** False when the business has no pay period to compare over. */
  comparisonPossible: boolean;
  busy: boolean;
}) => {
  const [confirming, setConfirming] = useState(false);
  // A closed proposal has no publishing left to do, so the control goes. An
  // empty one does: it is the thing this page is for, and showing it disabled
  // with its reason says "nothing to publish yet" in the place the reader will
  // look for it, rather than as a separate line of text beside it.
  if (!workspace.editable) return null;

  // Asking for a check that cannot be run is a gate nobody can pass. The
  // server reaches the same conclusion; this only spares the reader a refusal
  // they could not have acted on.
  const needsCheck =
    workspace.pay_may_move && comparisonPossible && !comparisonReady;
  const reason = !workspace.has_changes
    ? "No edits to publish"
    : busy
      ? "Wait for the current change to finish"
      : needsCheck
        ? "Run the comparison on the Pay tab first, so you can see what this does to pay"
        : "";

  return (
    <>
      {/* The compact pill, not the standard one. This sits in a 40px control
          strip beside the tabs, where `sm` at 40px tall with 24px of side
          padding filled the whole row and read as the loudest thing on the
          page. The dialog's own buttons stay full size, because that is a
          decision screen rather than a toolbar. */}
      {/* The reason rides a real tooltip, not the `title` attribute.
          A disabled button fires no pointer events, so the browser never
          showed one, and the button most often disabled was the one whose
          reason mattered most. `asDiv` puts the trigger on a wrapper so the
          disabled state cannot swallow the hover. */}
      <Tooltip>
        <TooltipTrigger asDiv className="shrink-0">
          <Button
            size="xsPill"
            disabled={!!reason || publish.isPending}
            loading={publish.isPending}
            onClick={() => {
              publish.reset();
              setConfirming(true);
            }}
          >
            Publish
          </Button>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          {reason || "Put these changes live"}
        </TooltipContent>
      </Tooltip>
      <Dialog open={confirming} onOpenChange={setConfirming}>
        <DialogContent className="max-w-md">
          <DialogHeader className="p-6 pb-0 text-left">
            <DialogTitle>Publish these changes?</DialogTitle>
            <DialogDescription className="not-sr-only pt-2">
              Your next payroll run will use these rules
              {workspace.pay_may_move ? " and pay people by them" : ""}. You can
              change them again at any time, and every past run keeps the rules
              it was run with.
            </DialogDescription>
          </DialogHeader>
          {/* The same reason the button outside carries. A request can start
              while this is open, and the dialog used to publish anyway: the
              owner would have put a half finished change live from a screen
              that had already been told not to. It says so rather than closing
              itself, because a dialog that vanishes under the cursor explains
              nothing. */}
          {!!(reason || publish.error) && (
            <p role="alert" className="px-6 text-sm text-negative-text">
              {reason || proposalError(publish.error)}
            </p>
          )}
          <DialogFooter className="gap-2 p-6">
            <Button
              size="sm"
              variant="tertiary"
              onClick={() => setConfirming(false)}
            >
              Not yet
            </Button>
            <Button
              size="sm"
              disabled={!!reason || publish.isPending}
              loading={publish.isPending}
              onClick={() =>
                publish.mutate(undefined, {
                  onSuccess: () => setConfirming(false),
                })
              }
            >
              Publish
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
